import { useEffect, useRef, useState } from "react";
import {
  pushRateSample,
  windowedRate,
  etaSeconds,
  percentComplete,
  formatRate,
  formatEta,
  formatBytes,
  type RateSample,
} from "../../utils/downloadProgress";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import { useProgressPoll } from "../../utils/useProgressPoll";
import { isCdromPromptOpen, subscribeCdromPrompt } from "../../utils/cdromPrompt";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface DownloadProgress {
  config: string;
  turbine: string;
  current: number;
  total: number;
  /** Version (config/bin) download → an app-wide Tiger update. */
  is_version: boolean;
  /** Version download that includes the binaries → shown as "Tiger" (else the
   *  turbine name for a config-only version download). */
  is_bin: boolean;
}

interface DownloadFinished {
  config: string;
  outcome: string;
}

/**
 * Shown while a Remote Data Download (RDD) is in flight. Opens on the first
 * `download-progress` event (emitted from `RDD_PROGRESS`), updates the bar /
 * transfer rate / ETA as more arrive, and closes on `download-finished`
 * (complete / cancelled / failed). The Cancel button issues `RDD_CANCEL_GET`
 * via the `cancel_download` command. Mirrors the legacy `Ui_download_progress`
 * dialog.
 *
 * Self-contained: manages its own visibility, so it only needs to be mounted
 * once. Downloads are serialized backend-side, so a single dialog instance
 * tracks the one in-flight transfer. Hidden while the CD-ROM Data Request
 * prompt is open (e.g. a fan-out parks the next turbine's request during a
 * download) — it mounts after DataRequestDialog on the same z-index and would
 * otherwise bury the prompt.
 */
export function DownloadProgressDialog() {
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [rate, setRate] = useState(0);
  const [eta, setEta] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const [cdromPrompt, setCdromPrompt] = useState(isCdromPromptOpen());

  useEffect(() => subscribeCdromPrompt(setCdromPrompt), []);

  // Rolling window of recent (time, cumulative-bytes) readings for the current
  // download, used to show a sliding-window transfer rate the way browsers do
  // (a moving average over the last few seconds) instead of a per-event rate
  // that jitters or a since-start average that drifts. Reset per download.
  const history = useRef<{ config: string; samples: RateSample[] } | null>(null);

  // Advance the bar / rate / ETA from a progress reading. Shared by the desktop
  // `download-progress` event and the web poll. Touches only refs and stable
  // setState, so it's safe to capture in the once-registered listener.
  const applyProgress = (p: DownloadProgress) => {
    const now = Date.now();
    const h = history.current;
    const last = h?.samples[h.samples.length - 1];
    // Start fresh for a new download (config changed, none yet, or the byte
    // count went backwards). Only anchor the window once data has actually
    // arrived (current > 0): the wait before the first chunk is transfer
    // setup, and counting it would drag the rate down / inflate the ETA —
    // until then keep showing "estimating…".
    if (!h || h.config !== p.config || (last && p.current < last.bytes)) {
      history.current = p.current > 0 ? { config: p.config, samples: [{ t: now, bytes: p.current }] } : null;
      setRate(0);
      setEta(0);
    } else {
      // Append every reading (including repeated web-poll values) and average
      // over the trimmed window: distinct readings give the rate, repeats just
      // extend the span so a stall eases the rate toward 0 rather than spiking.
      const samples = pushRateSample(h.samples, { t: now, bytes: p.current });
      history.current = { config: p.config, samples };
      const r = windowedRate(samples);
      setRate(r);
      setEta(etaSeconds(p.total, p.current, r));
    }
    setCancelling(false);
    setProgress(p);
  };

  // Terminal state (complete / cancelled / failed) → close the dialog.
  const applyFinished = () => {
    history.current = null;
    setProgress(null);
    setCancelling(false);
    setRate(0);
    setEta(0);
  };

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];

    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const offProgress = await listen<DownloadProgress>("download-progress", (e) => applyProgress(e.payload));
      if (cancelled) offProgress();
      else offs.push(offProgress);

      const offFinished = await listen<DownloadFinished>("download-finished", () => applyFinished());
      if (cancelled) offFinished();
      else offs.push(offFinished);
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  // Web: no event channel — poll the in-flight download's progress. A null poll
  // means the download finished (or none is running), which closes the dialog.
  const webDownload = useProgressPoll<DownloadProgress>(
    !isTauri() ? "/api/get_download_progress" : null,
  );
  useEffect(() => {
    if (isTauri()) return;
    if (webDownload) applyProgress(webDownload);
    else applyFinished();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webDownload]);

  if (!progress || cdromPrompt) return null;

  const pct = percentComplete(progress.current, progress.total);

  const onCancel = async () => {
    setCancelling(true);
    if (!isTauri()) {
      // Web: tell the server to cancel; the dialog closes when the next poll
      // returns null (download-finished has no web event channel).
      await fetch("/api/cancel_download");
      return;
    }
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("cancel_download");
    // The dialog closes when `download-finished` arrives.
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{getTigerResource("TIGER_DOWNLOAD_PROGRESS_TITLE")}</span>
          <button
            className="acrylic-close-btn"
            onClick={onCancel}
            disabled={cancelling}
            title={getTigerResource("TIGER_DOWNLOAD_CANCEL_TOOLTIP")}
          >
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.label}>
            {progress.is_version && progress.is_bin ? (
              <>{getTigerResource("TIGER_DOWNLOAD_DOWNLOADING")}<b>Tiger</b>&hellip;</>
            ) : progress.is_version ? (
              <>{getTigerResource("TIGER_DOWNLOAD_DOWNLOADING")}<b>{progress.turbine || progress.config}</b>&hellip;</>
            ) : (
              <>{getTigerResource("TIGER_DOWNLOAD_DOWNLOADING_DATA")}<b>{progress.turbine || progress.config}</b>&hellip;</>
            )}
          </div>
          <div style={styles.barOuter}>
            <div style={{ ...styles.barInner, width: `${pct}%` }} />
            <div style={styles.barLabel}>{pct.toFixed(0)}%</div>
          </div>
          <div style={styles.stats}>
            <span>
              {formatBytes(progress.current)}
              {progress.total > 0 ? ` / ${formatBytes(progress.total)}` : ""}
            </span>
            <span>{formatRate(rate)}</span>
            <span>{eta > 0 ? tigerFormat(getTigerResource("TIGER_DOWNLOAD_ETA_LEFT"), formatEta(eta)) : getTigerResource("TIGER_DOWNLOAD_ESTIMATING")}</span>
          </div>
          <div style={styles.buttons}>
            <button className="btn-acrylic-secondary" onClick={onCancel} disabled={cancelling}>
              {cancelling ? getTigerResource("TIGER_DOWNLOAD_CANCELLING") : getTigerResource("TIGER_RES_CANCEL")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  // Flex-centred overlay card (no fixed geometry), so the dialog keeps its own
  // centring mechanics and only the surface adopts the acrylic treatment.
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: "var(--ui-surface)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.65)",
    borderRadius: 14,
    width: 420,
    maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)",
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden",
  },
  barInner: {
    height: "100%",
    background: "var(--ui-btn-primary-bg)",
    transition: "width 0.2s ease",
  },
  barLabel: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#4b5260",
  },
  stats: {
    display: "flex",
    justifyContent: "space-between",
    color: "#4b5260",
    fontSize: 12,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
} as const;
